// Underlying-data modal (Phase 7): the rows behind a clicked visual point.
//
// Fully CLIENT-SIDE — the host hands it the `[schemaRow, ...rows]` dataset the
// visual already rendered (filtered to the clicked point by
// `underlyingClientRows`), so there is NO backend round-trip. It renders those
// rows in the shared GridTable using the visual's own layout (so columns keep
// their labels/formatting), with the clicked filters shown as chips.

import { useEffect } from 'react'
import { GridTable } from './GridTable'
import type { VisualJsonData, VisualLayout } from './types'

export interface UnderlyingDataModalProps {
  open: boolean
  onClose: () => void
  /** `[schemaRow, ...rows]` to show — already filtered to the clicked point. */
  jsonData: VisualJsonData
  /** The visual's layout (drives column order/labels/formatting in the grid). */
  visualLayout: VisualLayout
  /** Clicked-point equality filters (raw values), shown as chips. */
  filters: Record<string, unknown>
  /** Optional title (defaults to "Underlying data"). */
  title?: string
}

export function UnderlyingDataModal({
  open, onClose, jsonData, visualLayout, filters, title = 'Underlying data',
}: UnderlyingDataModalProps) {
  // Close on Escape while open; lock body scroll.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [open, onClose])

  if (!open) return null

  const chips = Object.entries(filters || {}).filter(([, v]) => v != null && v !== '')
  const rowCount = Math.max(0, (jsonData?.length ?? 1) - 1)

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10040, background: 'rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column', padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          flex: 1, minHeight: 0, borderRadius: 10, overflow: 'hidden',
          background: 'var(--color-bg, #fff)', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: title + filter chips + close */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          borderBottom: '1px solid var(--color-border, #e5e7eb)', flexShrink: 0,
          background: 'var(--color-bg-secondary, #f8f9fa)',
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text, #111)', flexShrink: 0 }}>{title}</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {chips.map(([k, v]) => (
              <span key={k} style={{
                fontSize: 11, padding: '1px 8px', borderRadius: 10,
                border: '1px solid var(--color-border, #e5e7eb)',
                background: 'var(--color-bg, #fff)', color: 'var(--color-text-secondary, #6b7280)',
                whiteSpace: 'nowrap',
              }}>
                {k} = {String(v)}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close (Esc)"
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--color-text-secondary, #6b7280)', flexShrink: 0, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Body: the detail grid */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 8 }}>
          {rowCount > 0 ? (
            <GridTable jsonData={jsonData} visualLayout={visualLayout} />
          ) : (
            <div style={{ padding: 16, color: 'var(--color-text-secondary, #6b7280)', fontSize: 13 }}>
              No underlying rows.
            </div>
          )}
        </div>

        {/* Footer: row count */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px',
          borderTop: '1px solid var(--color-border, #e5e7eb)', flexShrink: 0,
          fontSize: 12, color: 'var(--color-text-secondary, #6b7280)',
          background: 'var(--color-bg-secondary, #f8f9fa)',
        }}>
          <span>{rowCount} row{rowCount === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  )
}

export default UnderlyingDataModal
